import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getFolderAssets,
  getFolderInfo,
  listMediaFolders,
  uploadAsset,
  linkAsset,
  updateAsset,
  deleteAsset,
  deleteAudio,
  MediaAsset,
  MediaFolder,
} from '../../../../api/media';
import '../../../../assets/styles/media-manager.scss';
import '../../../../assets/styles/media-public-detail.scss';
import { MEDIA_BASE } from '../../../../api/config';

type KindFilter = '' | 'image' | 'audio' | 'video' | 'doc' | 'other';

function fmtBytes(b?: number | null) {
  if (!b && b !== 0) return '—';
  if (b < 1024) return `${b} B`;
  const kb = b / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
}

function isLinkAsset(a: MediaAsset) {
  return !!a.url && !a.file_path;
}

function resolveUrl(u?: string | null): string | undefined {
  if (!u) return undefined;
  if (/^https?:\/\//i.test(u)) return u;
  const clean = u.startsWith('/') ? u : `/${u}`;
  return `${MEDIA_BASE}/media${clean}`;
}

function assetPreviewSrc(a: MediaAsset): string | undefined {
  return resolveUrl(a.url || a.file_path || undefined);
}

/** Mapowanie BCP-47 -> nazwa języka (skrót najczęściej spotykany) */
function languageLabel(code?: string | null): string | null {
  if (!code) return null;
  const c = code.trim();
  const base = c.toLowerCase();
  const map: Record<string, string> = {
    'pl': 'polski',
    'en': 'angielski',
    'en-us': 'angielski (US)',
    'en-gb': 'angielski (GB)',
    'de': 'niemiecki',
    'fr': 'francuski',
    'es': 'hiszpański',
    'it': 'włoski',
    'ru': 'rosyjski',
    'uk': 'ukraiński',
    'cs': 'czeski',
    'sk': 'słowacki',
    'ro': 'rumuński',
    'hu': 'węgierski',
    'nl': 'niderlandzki',
    'sv': 'szwedzki',
    'no': 'norweski',
    'fi': 'fiński',
    'da': 'duński',
    'pt': 'portugalski',
    'pt-br': 'portugalski (BR)',
    'pt-pt': 'portugalski (PT)',
    'tr': 'turecki',
    'ar': 'arabski',
    'he': 'hebrajski',
    'zh': 'chiński',
    'zh-cn': 'chiński (CN)',
    'zh-tw': 'chiński (TW)',
    'ja': 'japoński',
    'ko': 'koreański',
    'el': 'grecki',
    'bg': 'bułgarski',
    'hr': 'chorwacki',
    'sr': 'serbski',
    'sl': 'słoweński',
    'lt': 'litewski',
    'lv': 'łotewski',
    'et': 'estoński',
  };
  // najpierw spróbuj pełnego, potem bazowego (np. en-GB -> en)
  return map[base] || map[base.split('-')[0]] || c;
}

const KindBadge = ({ kind }: { kind: MediaAsset['kind'] }) => {
  const label = { image: 'Obraz', audio: 'Audio', video: 'Wideo', doc: 'Dokument', other: 'Inne' }[kind] || kind;
  return <span className="badge">{label}</span>;
};

const IconDownload = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path d="M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

function PlaceholderIcon({ a }: { a: MediaAsset }) {
  if (isLinkAsset(a)) {
    return (
      <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden>
        <path d="M10 14a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M14 10a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }
  if (a.kind === 'audio') {
    return (
      <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden>
        <path d="M9 18V5l12-2v13" stroke="currentColor" strokeWidth="2" fill="none" />
        <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="2" fill="none" />
        <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="2" fill="none" />
      </svg>
    );
  }
  if (a.kind === 'video') {
    return (
      <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden>
        <rect x="3" y="5" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M17 8l4-2v12l-4-2z" stroke="currentColor" strokeWidth="2" fill="none" />
      </svg>
    );
  }
  if (a.kind === 'doc') {
    return (
      <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden>
        <path d="M6 2h9l5 5v15a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" fill="none" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden>
      <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
    </svg>
  );
}

export default function MediaPublicDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation() as any;
  const folderFromState: MediaFolder | undefined = location?.state?.folder;

  const folderId = useMemo(() => Number(id), [id]);

  // meta tekstu
  const [folder, setFolder] = useState<MediaFolder | null>(folderFromState || null);
  const [stats, setStats] = useState<Record<string, number>>({});

  // lista plików
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);

  // filtry
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<KindFilter>('');

  // upload/link
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [linkDesc, setLinkDesc] = useState('');
  const [linkKind, setLinkKind] = useState<KindFilter>('other');

  // edycja inline
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  // autoodświeżanie
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  // StrictMode guard + debounce
  const initializedRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  const startEdit = (a: MediaAsset) => {
    setEditingId(a.id);
    setEditName(a.file_name);
    setEditDesc(a.description || '');
    setAutoRefreshEnabled(false);
  };
  const cancelEdit = () => {
    setEditingId(null); setEditName(''); setEditDesc('');
    setAutoRefreshEnabled(true);
  };
  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await updateAsset(editingId, { file_name: editName.trim(), description: editDesc.trim() });
      cancelEdit();
      await loadAssets(q, kind);
    } catch (e: any) {
      alert(e?.response?.data?.error || e?.message || 'Błąd zapisu');
    }
  };

  const onDelete = async (a: MediaAsset) => {
    const txt = a.source === 'audio' ? 'Usunąć to audio?' : 'Usunąć to medium?';
    if (!window.confirm(txt)) return;
    try {
      if (a.source === 'audio') await deleteAudio(a.id);
      else await deleteAsset(a.id);
      await loadAssets(q, kind);
    } catch (e: any) {
      alert(e?.response?.data?.error || e?.message || 'Błąd usuwania');
    }
  };

  const onPickFile = () => {
    if (fileRef.current) fileRef.current.value = '';
    fileRef.current?.click();
  };
  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f || !folderId) return;
    try {
      await uploadAsset(folderId, f);
      await loadAssets(q, kind);
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || 'Błąd uploadu');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const addLink = async () => {
    if (!folderId) return;
    if (!linkUrl.trim()) return;
    try {
      await linkAsset(folderId, {
        url: linkUrl.trim(),
        file_name: linkName.trim() || undefined,
        description: linkDesc.trim() || undefined,
        kind: linkKind || 'other',
      });
      setLinkOpen(false);
      setLinkUrl(''); setLinkName(''); setLinkDesc(''); setLinkKind('other');
      setAutoRefreshEnabled(true);
      await loadAssets(q, kind);
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || 'Błąd dodawania linku');
    }
  };

  const loadFolderMetaIfNeeded = async () => {
    if (folder) return;
    const res = await listMediaFolders({ limit: 1000, offset: 0 });
    const items: MediaFolder[] = res.items || [];
    const found = items.find(x => x.folder_id === folderId) || null;
    setFolder(found);
  };

  const loadAssets = async (query: string, k: KindFilter) => {
    if (!folderId) return;
    setLoading(true);
    try {
      const res = await getFolderAssets(folderId, {
        q: query || undefined,
        kind: k || undefined,
        limit: 1000,
        offset: 0,
      });
      setAssets(res.items || []);

      const info = await getFolderInfo(folderId); // { content_id, total, by_kind }
      setStats(info.by_kind || {});

      await loadFolderMetaIfNeeded();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!autoRefreshEnabled) return;
    const delay = initializedRef.current ? 300 : 0;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      if (!initializedRef.current) initializedRef.current = true;
      loadAssets(q, kind);
    }, delay) as unknown as number;
    return () => { if (timerRef.current) window.clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId, q, kind, autoRefreshEnabled]);

  useEffect(() => {
    setAutoRefreshEnabled(!linkOpen && editingId === null);
  }, [linkOpen, editingId]);

  // grupowanie do list
  const grouped = useMemo(() => {
    const byKind: Record<MediaAsset['kind'], MediaAsset[]> = {
      image: [],
      audio: [],
      video: [],
      doc: [],
      other: [],
    };
    for (const a of assets) (byKind[a.kind] || byKind.other).push(a);
    return byKind;
  }, [assets]);

  const LineMeta: React.FC<{ a: MediaAsset }> = ({ a }) => (
    <div className="mpd-meta">
      {isLinkAsset(a) ? <span>Link</span> : <span>{fmtBytes(a.byte_size)}</span>}
      <span>•</span>
      <span>{new Date(a.created_at).toLocaleDateString()}</span>
      {a.mime_type && (<><span>•</span><span>{a.mime_type}</span></>)}
    </div>
  );

  const SectionHeader: React.FC<{ title: string; count: number }> = ({ title, count }) => (
    <h3 className="mpd-section-title">
      {title} <span className="badge">{count}</span>
    </h3>
  );

  const DocList: React.FC = () => (
    <ul className="mpd-list">
      {grouped.doc.map(a => {
        const href = resolveUrl(a.file_path || a.url);
        const isEditing = editingId === a.id && a.source === 'media';
        return (
          <li key={`${a.source}-${a.id}`} className="mpd-item">
            <div className="mpd-row">
              <div className="mpd-icon"><PlaceholderIcon a={a} /></div>
              <div className="mpd-body">
                <div className="mpd-line">
                  {href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="mpd-name">
                      {a.file_name || 'Dokument'}
                    </a>
                  ) : (
                    <span className="mpd-name">{a.file_name || 'Dokument'}</span>
                  )}
                  <KindBadge kind={a.kind} />
                </div>
                {a.description && <div className="mpd-desc">{a.description}</div>}
                <LineMeta a={a} />
              </div>

              <div className="mpd-actions">
                {(a.file_path || a.url) && (
                  <a
                    className="icon-btn download"
                    href={href}
                    download={!isLinkAsset(a)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={isLinkAsset(a) ? 'Otwórz link' : 'Pobierz'}
                    aria-label={isLinkAsset(a) ? 'Otwórz link' : 'Pobierz'}
                  >
                    <IconDownload />
                  </a>
                )}
                {a.source === 'media' && !isEditing && (
                  <>
                    <button className="icon-btn edit" title="Edytuj" aria-label="Edytuj" onClick={() => startEdit(a)}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
                      </svg>
                    </button>
                    <button className="icon-btn delete" title="Usuń" aria-label="Usuń" onClick={() => onDelete(a)}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18"></path>
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </div>

            {isEditing && (
              <div className="mpd-edit">
                <input
                  className="mm-input"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  placeholder="Nazwa pliku"
                />
                <textarea
                  className="mm-textarea"
                  value={editDesc}
                  onChange={e => setEditDesc(e.target.value)}
                  placeholder="Opis"
                  rows={2}
                />
                <div className="mpd-edit-actions">
                  <button className="btn small" onClick={saveEdit}>Zapisz</button>
                  <button className="btn small ghost" onClick={cancelEdit}>Anuluj</button>
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  const AudioList: React.FC = () => (
    <div className="mpd-col">
      {grouped.audio.map(a => {
        const src = resolveUrl(a.file_path || a.url);
        const audioType = (a.mime_type && a.mime_type.startsWith('audio/')) ? a.mime_type : 'audio/mpeg';
        const isEditing = editingId === a.id && a.source === 'media';
        const lang = languageLabel((a as any).language_code);
        return (
          <div key={`${a.source}-${a.id}`} className="mpd-block">
            {/* elegancka karta odtwarzacza */}
            <div className="mpd-audio-card">
              <div className="mpd-audio-head">
                <div className="mpd-audio-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path d="M9 18V5l12-2v13" stroke="currentColor" strokeWidth="2" fill="none" />
                    <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="2" fill="none" />
                    <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="2" fill="none" />
                  </svg>
                </div>
                <div className="mpd-audio-title">{a.file_name || 'Audio'}</div>
                {lang && <span className="badge badge-lang" title={`Język nagrania: ${lang}`}>{lang}</span>}
              </div>
              <div className="mpd-audio">
                {src ? (
                  <audio controls preload="metadata" crossOrigin="anonymous">
                    <source src={src} type={audioType} />
                  </audio>
                ) : (
                  <div className="mpd-ph"><PlaceholderIcon a={a} /></div>
                )}
              </div>
            </div>

            <div className="mpd-body">
              <div className="mpd-line">
                <KindBadge kind={a.kind} />
                {src && (
                  <a
                    className="icon-btn download"
                    href={src}
                    download={!isLinkAsset(a)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={isLinkAsset(a) ? 'Otwórz link' : 'Pobierz'}
                    aria-label={isLinkAsset(a) ? 'Otwórz link' : 'Pobierz'}
                  >
                    <IconDownload />
                  </a>
                )}
                {a.source === 'media' && !isEditing && (
                  <>
                    <button className="icon-btn edit" title="Edytuj" aria-label="Edytuj" onClick={() => startEdit(a)}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
                      </svg>
                    </button>
                    <button className="icon-btn delete" title="Usuń" aria-label="Usuń" onClick={() => onDelete(a)}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18"></path>
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                      </svg>
                    </button>
                  </>
                )}
              </div>
              {a.description && <div className="mpd-desc">Lektor: {a.description}</div>}
              <LineMeta a={a} />
            </div>

            {isEditing && (
              <div className="mpd-edit">
                <input
                  className="mm-input"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  placeholder="Nazwa pliku"
                />
                <textarea
                  className="mm-textarea"
                  value={editDesc}
                  onChange={e => setEditDesc(e.target.value)}
                  placeholder="Opis"
                  rows={2}
                />
                <div className="mpd-edit-actions">
                  <button className="btn small" onClick={saveEdit}>Zapisz</button>
                  <button className="btn small ghost" onClick={cancelEdit}>Anuluj</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const ImageList: React.FC = () => (
    <div className="mpd-col">
      {grouped.image.map(a => {
        const thumb = assetPreviewSrc(a);
        const href = resolveUrl(a.file_path || a.url);
        const isEditing = editingId === a.id && a.source === 'media';
        return (
          <div key={`${a.source}-${a.id}`} className="mpd-block">
            <div
              className="mpd-media"
              onClick={() => href && window.open(href, '_blank', 'noopener,noreferrer')}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && href) window.open(href, '_blank', 'noopener,noreferrer'); }}
            >
              {thumb ? <img src={thumb} alt={a.file_name} /> : <div className="mpd-ph"><PlaceholderIcon a={a} /></div>}
            </div>

            <div className="mpd-body">
              <div className="mpd-line">
                <span className="mpd-name">{a.file_name || 'Obraz'}</span>
                <KindBadge kind={a.kind} />
                {href && (
                  <a
                    className="icon-btn download"
                    href={href}
                    download={!isLinkAsset(a)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={isLinkAsset(a) ? 'Otwórz link' : 'Pobierz'}
                    aria-label={isLinkAsset(a) ? 'Otwórz link' : 'Pobierz'}
                  >
                    <IconDownload />
                  </a>
                )}
                {a.source === 'media' && !isEditing && (
                  <>
                    <button className="icon-btn edit" title="Edytuj" aria-label="Edytuj" onClick={() => startEdit(a)}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
                      </svg>
                    </button>
                    <button className="icon-btn delete" title="Usuń" aria-label="Usuń" onClick={() => onDelete(a)}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18"></path>
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                      </svg>
                    </button>
                  </>
                )}
              </div>
              {a.description && <div className="mpd-desc">{a.description}</div>}
              <LineMeta a={a} />
            </div>

            {isEditing && (
              <div className="mpd-edit">
                <input className="mm-input" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Nazwa pliku" />
                <textarea className="mm-textarea" value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Opis" rows={2} />
                <div className="mpd-edit-actions">
                  <button className="btn small" onClick={saveEdit}>Zapisz</button>
                  <button className="btn small ghost" onClick={cancelEdit}>Anuluj</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const VideoList: React.FC = () => (
    <div className="mpd-col">
      {grouped.video.map(a => {
        const href = resolveUrl(a.file_path || a.url);
        const videoType = (a.mime_type && a.mime_type.startsWith('video/')) ? a.mime_type : 'video/mp4';
        const isEditing = editingId === a.id && a.source === 'media';
        return (
          <div key={`${a.source}-${a.id}`} className="mpd-block">
            <div className="mpd-media">
              {a.file_path ? (
                <video controls preload="metadata">
                  <source src={resolveUrl(a.file_path)} type={videoType} />
                </video>
              ) : (
                <div
                  className="mpd-ph mpd-link"
                  onClick={() => href && window.open(href, '_blank', 'noopener,noreferrer')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && href) window.open(href, '_blank', 'noopener,noreferrer'); }}
                  title={href ? 'Otwórz' : undefined}
                >
                  <PlaceholderIcon a={a} />
                </div>
              )}
            </div>

            <div className="mpd-body">
              <div className="mpd-line">
                <span className="mpd-name">{a.file_name || 'Wideo'}</span>
                <KindBadge kind={a.kind} />
                {href && (
                  <a
                    className="icon-btn download"
                    href={href}
                    download={!isLinkAsset(a)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={isLinkAsset(a) ? 'Otwórz link' : 'Pobierz'}
                    aria-label={isLinkAsset(a) ? 'Otwórz link' : 'Pobierz'}
                  >
                    <IconDownload />
                  </a>
                )}
                {a.source === 'media' && !isEditing && (
                  <>
                    <button className="icon-btn edit" title="Edytuj" aria-label="Edytuj" onClick={() => startEdit(a)}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
                      </svg>
                    </button>
                    <button className="icon-btn delete" title="Usuń" aria-label="Usuń" onClick={() => onDelete(a)}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18"></path>
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                      </svg>
                    </button>
                  </>
                )}
              </div>
              {a.description && <div className="mpd-desc">{a.description}</div>}
              <LineMeta a={a} />
            </div>

            {isEditing && (
              <div className="mpd-edit">
                <input className="mm-input" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Nazwa pliku" />
                <textarea className="mm-textarea" value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Opis" rows={2} />
                <div className="mpd-edit-actions">
                  <button className="btn small" onClick={saveEdit}>Zapisz</button>
                  <button className="btn small ghost" onClick={cancelEdit}>Anuluj</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const OtherList: React.FC = () => (
    <ul className="mpd-list">
      {grouped.other.map(a => {
        const href = resolveUrl(a.file_path || a.url);
        const isEditing = editingId === a.id && a.source === 'media';
        return (
          <li key={`${a.source}-${a.id}`} className="mpd-item">
            <div className="mpd-row">
              <div className="mpd-icon"><PlaceholderIcon a={a} /></div>
              <div className="mpd-body">
                <div className="mpd-line">
                  {href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="mpd-name">
                      {a.file_name || 'Plik'}
                    </a>
                  ) : (
                    <span className="mpd-name">{a.file_name || 'Plik'}</span>
                  )}
                  <KindBadge kind={a.kind} />
                </div>
                {a.description && <div className="mpd-desc">{a.description}</div>}
                <LineMeta a={a} />
              </div>

              <div className="mpd-actions">
                {(a.file_path || a.url) && (
                  <a
                    className="icon-btn download"
                    href={href}
                    download={!isLinkAsset(a)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={isLinkAsset(a) ? 'Otwórz link' : 'Pobierz'}
                    aria-label={isLinkAsset(a) ? 'Otwórz link' : 'Pobierz'}
                  >
                    <IconDownload />
                  </a>
                )}
                {a.source === 'media' && !isEditing && (
                  <>
                    <button className="icon-btn edit" title="Edytuj" aria-label="Edytuj" onClick={() => startEdit(a)}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
                      </svg>
                    </button>
                    <button className="icon-btn delete" title="Usuń" aria-label="Usuń" onClick={() => onDelete(a)}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18"></path>
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </div>

            {isEditing && (
              <div className="mpd-edit">
                <input className="mm-input" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Nazwa pliku" />
                <textarea className="mm-textarea" value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Opis" rows={2} />
                <div className="mpd-edit-actions">
                  <button className="btn small" onClick={saveEdit}>Zapisz</button>
                  <button className="btn small ghost" onClick={cancelEdit}>Anuluj</button>
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  const anyVisible =
    grouped.doc.length || grouped.audio.length || grouped.image.length || grouped.video.length || grouped.other.length;

  return (
    <div className="mm-box">
      <div className="mm-header">
        <div className="mpd-header-left">
          <button className="btn" onClick={() => navigate('/admin/media')}>←</button>
          <h2>
            {(folder?.folder_id ?? folderId)}. {(folder?.folder_title ?? 'Tekst')}
          </h2>
          <div className="mm-stats mpd-stats">
            <span className="badge">Obrazy: {stats?.image ?? 0}</span>
            <span className="badge">Audio: {stats?.audio ?? 0}</span>
            <span className="badge">Wideo: {stats?.video ?? 0}</span>
            <span className="badge">Dokumenty: {stats?.doc ?? 0}</span>
            <span className="badge">Inne: {stats?.other ?? 0}</span>
          </div>
        </div>

        <div className="mm-actions">
          <div className="mm-actions-search">
            <input
              className="mm-search"
              placeholder="Szukaj w plikach tego tekstu…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
            <select className="mm-kind" value={kind} onChange={e => setKind(e.target.value as KindFilter)}>
              <option value="">Rodzaj</option>
              <option value="image">Obrazy</option>
              <option value="audio">Audio</option>
              <option value="video">Wideo</option>
              <option value="doc">Dokumenty</option>
              <option value="other">Inne</option>
            </select>
          </div>

          <div className="mm-actions-files">
            <input type="file" ref={fileRef} style={{ display: 'none' }} onChange={onFilePicked} />
            <button className="btn" onClick={onPickFile}>Dodaj plik</button>
            <button className="btn" onClick={() => setLinkOpen(true)}>Dodaj link</button>
          </div>
        </div>
      </div>

      {loading && <div className="mm-loading">Ładowanie…</div>}
      {!loading && !anyVisible && <div className="mm-empty">Brak plików w tym tekstu</div>}

      {!loading && anyVisible && (
        <div className="mpd-sections">
          {grouped.doc.length > 0 && (<><SectionHeader title="Dokumenty" count={grouped.doc.length} /><DocList /></>)}
          {grouped.audio.length > 0 && (<><SectionHeader title="Audio" count={grouped.audio.length} /><AudioList /></>)}
          {grouped.image.length > 0 && (<><SectionHeader title="Obrazy" count={grouped.image.length} /><ImageList /></>)}
          {grouped.video.length > 0 && (<><SectionHeader title="Wideo" count={grouped.video.length} /><VideoList /></>)}
          {grouped.other.length > 0 && (<><SectionHeader title="Inne" count={grouped.other.length} /><OtherList /></>)}
        </div>
      )}

      {/* Modal: dodaj link */}
      {linkOpen && (
        <div className="modal-overlay" onClick={() => setLinkOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Dodaj link</h3>
            <div className="modal-body">
              <input className="mm-input" placeholder="URL" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} />
              <input className="mm-input" placeholder="Nazwa (opcjonalnie)" value={linkName} onChange={e => setLinkName(e.target.value)} />
              <textarea className="mm-textarea" placeholder="Opis (opcjonalnie)" rows={3} value={linkDesc} onChange={e => setLinkDesc(e.target.value)} />
              <select className="mm-select" value={linkKind} onChange={e => setLinkKind(e.target.value as KindFilter)}>
                <option value="other">Rodzaj (domyślnie Inne)</option>
                <option value="image">Obraz</option>
                <option value="audio">Audio</option>
                <option value="video">Wideo</option>
                <option value="doc">Dokument</option>
                <option value="other">Inne</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={addLink}>Dodaj</button>
              <button className="btn ghost" onClick={() => { setLinkOpen(false); setAutoRefreshEnabled(true); }}>Anuluj</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
